<template>
  <div style="position: relative">
    <!-- Marcador fixo central quando tooltip está oculto -->
    <div class="lecard-marker" v-show="!showTooltip">
      <svg width="49" height="48" viewBox="0 0 49 48" xmlns="http://www.w3.org/2000/svg">
        <path d="M25 45Q7 32.94 7 21c0-9.941 8.059-18 18-18s18 8.059 18 18q0 11.94-18 24m0-16a8 8 0 1 0 0-16 8 8 0 0 0 0 16" fill="#3F3E3E"/>
      </svg>
    </div>

    <!-- Mensagem de erro -->
    <aviso-informativo variante="vermelho" :mostrar-icone="false" class="message-error" v-if="msgErro">
      {{ msgErro }}
    </aviso-informativo>

    <!-- Botão de confirmar localização -->
    <div class="btn-map" v-if="!msgErro && showMap">
      <button class="btn btn-primary btn-block py-2" @click="confirmar">
        Confirmar localização
      </button>
    </div>

    <!-- Mapa -->
    <div class="map-container" v-if="center">
      <l-map
          ref="map"
          :zoom="zoom"
          :center="center"
          :options="mapOptions"
          @movestart="movestart"
          @moveend="moveend"
          @ready="onMapReady">
        <l-tile-layer :url="url" :attribution="attribution" />

        <!-- Marcador do usuário -->
        <l-marker v-if="showTooltip && userLocation"
            :lat-lng="userLocation"
            :icon="customIcon">
          <l-tooltip :options="tooltipOption">
            <div class="text-center">
              <div class="font-weight-bold">Você está aqui</div>
              <small>Mova para ajustar sua posição</small>
            </div>
          </l-tooltip>
        </l-marker>

        <!-- Círculo feedback (muda de azul para vermelho conforme distância) -->
        <l-circle
            :lat-lng="center"
            :radius="raio_usuario"
            :weight="0"
            :fillOpacity="0.01"
            @ready="onCircleReady"/>

        <!-- Raio de entrega da empresa -->
        <l-circle v-if="empresa && raio_empresa && !dados.id_area"
            :lat-lng="empresa"
            :radius="raio_empresa"
            :weight="2"
            :fillOpacity="0.04"
            fillColor="#3399ff"
            color="#0066ff"/>

        <l-geo-json :geojson="geo_areas" :options-style="styleFunction" v-if="geo_areas"/>

      </l-map>
    </div>

    <!-- Loading -->
    <div v-if="!showMap" class="mt-5">
      <loading fullscrean="true" text="Carregando mapa" />
    </div>
  </div>
</template>

<script>
import AvisoInformativo from "../AvisoInformativo";
import { latLng, Icon } from "leaflet";
import { LMap, LTileLayer, LMarker, LTooltip, LCircle, LGeoJson } from "vue2-leaflet";
import booleanPointInPolygon from '@turf/boolean-point-in-polygon'
import { point } from '@turf/helpers'

import imgIcon from "../../assets/map.svg";
import Loading from "../Loading";

// configuração do marcador padrão (Leaflet)
delete Icon.Default.prototype._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png")
});

export default {
  name: "MapLeaflet",
  components: {AvisoInformativo,  Loading, LMap, LTileLayer, LMarker, LTooltip, LCircle, LGeoJson },
  props: ["dados"],

  data() {
    return {
      map: null,
      circle: null,
      showMap: false,
      msgErro: "",
      isInside: true,

      center: null,
      userLocation: null,

      empresa: null,
      raio_empresa: null,
      raio_usuario: null,
      geo_areas: null,

      showTooltip: true,

      customIcon: new Icon({
        iconUrl: imgIcon,
        iconSize: [48, 48],
        iconAnchor: [24, 48]
      }),

      zoom: 16,

      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      mapOptions: {
        zoomSnap: 0.5,
        minZoom: 12,
        maxZoom: 18,
        zoomControl: true,
        scrollWheelZoom: "center"
      },

      tooltipOption: {
        permanent: true,
        direction: "top",
        offset: [0, -48]
      }
    };
  },

  methods: {
    getDistance(a, b) {
      return this.map ? this.map.distance(a, b) : null;
    },

    onMapReady(mapInstance) {
      if (this.showMap) return;

      this.showMap = true;
      this.map = mapInstance;
      this.validarDistancia();
    },

    movestart() {
      this.showTooltip = false;
    },

    moveend(e) {
      this.showTooltip = true;
      const center = e.target.getCenter();
      this.userLocation = center;

      // valida primeiro o raio da empresa
      if (!this.validarDistancia()) return;

      // depois valida círculo do usuário
      this.isInsideCircle(center);

      if (this.isInside) {
        this.msgErro = '';
      }
    },

    onCircleReady(circle) {
      this.circle = circle;
    },

    isInsideCircle(posicao) {
      const distance = this.getDistance(posicao, this.center);
      this.isInside = distance < this.circle.getRadius();

      if (!this.isInside) {
        this.msgErro = "Muito distante do lugar que informou!";
      }

      // estilo do círculo
      this.circle.setStyle(
          this.isInside
              ? { fillColor: "#3388ff", color: "#3388ff", fillOpacity: 0, weight: 0 }
              : { fillColor: "#f03", color: "#f03", fillOpacity: 0.3, weight: 1 }
      );
    },

    validarDistancia() {
      if (!this.map) {
        return false;
      }

      const distance = this.getDistance(this.empresa, this.userLocation);

      if (this.raio_empresa && distance > this.raio_empresa) {
        this.msgErro = "Infelizmente não entregamos nesta região!";
        return false;
      }

      if (this.geo_areas) {
        const ponto = point([this.userLocation.lng, this.userLocation.lat]);
        const isInside = booleanPointInPolygon(ponto, this.geo_areas);

        if (!isInside) {
          this.msgErro = "Infelizmente não entregamos neste local!";
          return false;
        }
      }

      return true;
    },

    confirmar() {
      const dentroEmpresa = this.validarDistancia();

      if (!dentroEmpresa || !this.isInside) {
        return;
      }

      this.$emit("editarDados", this.userLocation);
      this.remove();
    },

    remove() {
      this.msgErro = "";
      this.center = null;
      this.showMap = false;
      this.isInside = true;
    },

    openMapa(endereco) {
      if (!endereco || !this.dados.lat_empresa || !this.dados.long_empresa) {
        this.remove();
        return;
      }

      this.msgErro = "";
      this.isInside = true;
      const lat_endereco = endereco.lat_endereco;
      const long_endereco = endereco.long_endereco;
      const local = latLng(lat_endereco, long_endereco);

      if (!local) {
        return;
      }

      // calcular ponto da empresa
      this.empresa = latLng(this.dados.lat_empresa, this.dados.long_empresa);
      this.zoom = endereco.buscar_mapa ? parseInt(this.dados.raio_cidade || 12000) >= 12000 ? 13 : 14.5 : 16;

      // desativar se a empresa tiver com a geo disabled
      if (this.dados.geo_disabled === '0') {
        this.raio_empresa = parseInt(this.dados.raio_empresa);
      }

      this.raio_usuario = parseInt(endereco.buscar_mapa ? this.dados.raio_cidade : 600);
      this.userLocation = local;

      if (this.dados.id_area) {
        this.getArea(local);

      } else {
        this.center = local;
      }
    },

    getArea(local) {
      this.$http.get(this.$store.state.base_url + 'geo-areas')
        .then(response => {
          this.geo_areas = response.data || null;

          if (this.geo_areas) {
            this.center = local;
          }

        }, res => {
          console.log(res)
        });
    },
  },

  computed: {
    styleFunction() {
      return {
        weight: 2,
        color: "#0066ff",
        fillColor: '#3399ff',
        fillOpacity: 0.04
      }
    },
  }
};
</script>

<style>
.lecard-marker {
  position: absolute;
  top: calc(50% - 28px);
  left: 50%;
  z-index: 99999;
  transform: translate(-50%, -50%);
}

.aviso-informativo.message-error {
  position: absolute;
  bottom: 50px;
  left: 50%;
  z-index: 99999;
  transform: translateX(-50%);
  width: 90%;
  max-width: 400px;
  text-align: center;
}

.btn-map {
  position: absolute;
  bottom: 50px;
  right: 0;
  left: 0;
  margin: 0 auto;
  z-index: 99999;
  width: 90%;
  max-width: 300px;
}

.map-container {
  width: 100%;
  height: calc(80vh - 57px);
}

@media (max-width: 767px) {
  .map-container {
    height: calc(100vh - 72px);
  }

  .btn-map {
    position: fixed;
  }
}
</style>
